/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect } from "react";
import { FiArrowRight, FiPackage, FiChevronLeft, FiShoppingBag, FiInfo } from "react-icons/fi";
import { formatNumber } from "../../utils/formatNumber";

export default function OrdersModal({ show, onClose, orders, openOrderDetails, theme }) {

    // منع التمرير في الصفحة الخلفية عند فتح قائمة الطلبات
    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [show]);

    if (!show) return null;

    const validOrders = orders
        .filter(order => order.orderNo != null && order.cityId)
        .map(order => ({
            ...order,
            orderNoStr: String(order.orderNo || "").trim()
        }));

    const calculateTotal = (order) => {
        if (order.netAmount > 0) return order.netAmount;
        return (order.details || []).reduce((sum, item) => sum + (Number(item.lineTotal) || (Number(item.quantity) * Number(item.price)) || 0), 0);
    };

    return (
        /* الطبقة الرئيسية: تغطي الشاشة بالكامل */
        <div className="fixed inset-0 z-[35000] bg-gray-50 flex flex-col animate-fadeIn" dir="rtl">

            {/* Header ثابت - تصميم صفحة مستقلة */}
            <div className="w-full bg-white border-b border-gray-100 sticky top-0 z-[40000] shadow-sm">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-gray-600 hover:bg-gray-100 transition-all active:scale-90"
                        >
                            <FiArrowRight size={24} />
                        </button>
                        <div className="flex flex-col">
                            <h3 className="text-xl md:text-2xl font-black text-gray-800">طلباتي السابقة</h3>
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${theme.bg}`}></span>
                                <span className="text-xs md:text-sm text-gray-400 font-bold">لديك {validOrders.length} طلبات مسجلة</span>
                            </div>
                        </div>
                    </div>

                    <div className={`hidden md:flex items-center justify-center w-12 h-12 rounded-2xl bg-opacity-10 ${theme.bg} ${theme.text}`}>
                        <FiPackage size={24} />
                    </div>
                </div>
            </div>

            {/* محتوى قائمة الطلبات */}
            <div className="flex-1 overflow-y-auto bg-gray-50/50">
                <div className="max-w-5xl mx-auto p-4 md:p-8">

                    {validOrders.length === 0 ? (
                        /* حالة عدم وجود طلبات */
                        <div className="flex flex-col items-center justify-center py-32 text-center bg-white rounded-[3rem] shadow-sm border border-gray-100 mt-10">
                            <div className="w-24 h-24 md:w-32 md:h-32 bg-gray-50 rounded-full flex items-center justify-center mb-6 border border-gray-100">
                                <FiShoppingBag className="text-5xl text-gray-200" />
                            </div>
                            <h3 className="text-xl font-black text-gray-800 mb-2">قائمة الطلبات فارغة</h3>
                            <p className="text-sm text-gray-400 max-w-xs leading-relaxed">
                                لم تقم بإجراء أي طلبات بعد. ابدأ بالتسوق الآن لتظهر طلباتك هنا!
                            </p>
                            <button
                                onClick={onClose}
                                className={`mt-8 px-8 py-3 rounded-2xl text-white font-bold shadow-lg transition-transform active:scale-95 ${theme.bg}`}
                            >
                                تصفح المتجر
                            </button>
                        </div>
                    ) : (
                        /* عرض الطلبات */
                        <div className="grid gap-4 md:gap-6">
                            {validOrders.map((order) => (
                                <div
                                    key={`${order.orderId}-${order.orderNo}`}
                                    className="group bg-white border border-gray-100 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] flex flex-col md:flex-row justify-between items-stretch md:items-center gap-6 transition-all hover:shadow-xl hover:shadow-gray-200/40 hover:border-gray-200"
                                >
                                    {/* الجزء الأيمن: رقم الطلب والحالة */}
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-50 rounded-[1.5rem] flex items-center justify-center text-gray-400 group-hover:bg-white group-hover:shadow-inner transition-all flex-shrink-0 border border-gray-100 relative">
                                            <span className="text-sm md:text-base font-black text-gray-800">#{order.orderNoStr.slice(-3)}</span>
                                            <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${getStatusColor(order.status)}`}></div>
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-base md:text-lg font-black text-gray-800">طلب رقم #{order.orderNoStr || "—"}</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs font-bold text-gray-400">حالة الطلب:</span>
                                                <span className={`text-xs font-black px-3 py-0.5 rounded-lg bg-gray-50 ${theme.text}`}>
                                                    {order.status || "قيد المعالجة"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* الجزء الأيسر: السعر والإجراءات */}
                                    <div className="flex items-center justify-between md:justify-end gap-6 md:gap-12 pt-5 md:pt-0 border-t border-gray-50 md:border-t-0">
                                        <div className="flex flex-col items-start md:items-end">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">المبلغ الإجمالي</span>
                                            <span className={`text-xl md:text-2xl font-black ${theme.text}`}>
                                                {formatNumber(calculateTotal(order))} <span className="text-xs">د.ع</span>
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => openOrderDetails(order)}
                                            className="flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 rounded-2xl bg-gray-900 text-white text-sm font-bold hover:bg-black transition-all active:scale-95 shadow-xl shadow-gray-200"
                                        >
                                            <FiInfo size={18} className="hidden sm:block" />
                                            <span>عرض التفاصيل</span>
                                            <FiChevronLeft size={20} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const getStatusColor = (status) => {
    if (!status) return "bg-gray-400";
    const s = status.toLowerCase();
    if (s.includes("مكتمل") || s.includes("تم") || s.includes("delivered")) return "bg-green-500";
    if (s.includes("ملغي") || s.includes("رفض") || s.includes("cancel")) return "bg-red-500";
    if (s.includes("شحن") || s.includes("طريق") || s.includes("shipping")) return "bg-blue-500";
    return "bg-amber-500";
}